import {inject, Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {HttpClient, HttpParams} from '@angular/common/http';
import {IFileStorageSearchParams} from '../models/IFileStorageSearchParams.interface';
import {IPaginationOptions} from '../models/IPaginationOptions.interface';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {CompaniesService} from '../../services/companies.service';
import {IDocumentCategory, IDocumentTypes, IOrganizationTaxonomyItem} from '../models/IDocumentType.interface';
import {IFileStorageEntry} from '../models/IFileStorageEntry.interface';
import {capitalizeFirstLetter} from '../utils/utils';
import {IFileStoragePaginated, IFileStorageItem} from '../models/IFileStoragePaginated.interface';
import {tap} from "rxjs/operators";

@Injectable({
  providedIn: 'root'
})
export class FileStorageService {

  private readonly url = environment.apiConfig.corporateAPI.endpoint;
  private readonly httpClient = inject(HttpClient);
  private readonly companiesService = inject(CompaniesService);
  private types$ = new BehaviorSubject<IDocumentTypes>({} as IDocumentTypes);
  private uploadComplete$ = new Subject<IFileStorageItem>();

  private get companyId(): number {
    return this.companiesService.managedCompanyId;
  }

  // ========== DOCUMENT TYPES/TAXONOMY METHODS ==========

  trackUpload(): Observable<IFileStorageItem> {
    return this.uploadComplete$.asObservable();
  }

  getTypesAsObs(): Observable<IDocumentTypes> {
    return this.types$.asObservable();
  }

  getTypes(): Observable<IDocumentTypes> {
    return this.httpClient.get<IDocumentTypes>(this.url + `/api/companies/${this.companyId}/dms/types`).pipe(
      tap(res => this.types$.next(res))
    );
  }

  createType(category: IDocumentCategory): Observable<IDocumentCategory> {
    return this.httpClient.post<IDocumentCategory>(this.url + `/api/companies/${this.companyId}/dms/types`, category);
  }

  updateType(taxonomyId: string, category: IDocumentCategory, companyId?: number): Observable<IDocumentCategory> {
    const targetCompanyId = companyId || this.companyId;
    return this.httpClient.put<IDocumentCategory>(
      this.url + `/api/companies/${targetCompanyId}/dms/types/${taxonomyId}`,
      category
    );
  }

  deleteType(taxonomyId: string, companyId?: number): Observable<void> {
    const targetCompanyId = companyId || this.companyId;
    return this.httpClient.delete<void>(
      this.url + `/api/companies/${targetCompanyId}/dms/types/${taxonomyId}`
    );
  }

  /**
   * Get taxonomy items for all companies in the organization
   * GET /api/organization/taxonomy-items
   */
  getOrganizationTaxonomyItems(): Observable<IOrganizationTaxonomyItem[]> {
    return this.httpClient.get<IOrganizationTaxonomyItem[]>(
      this.url + `/api/organization/taxonomy-items`
    );
  }

  // ========== DOCUMENT METHODS ==========

  uploadFile(entry: IFileStorageEntry, submittedFormId: number, companyId?: number): Observable<IFileStorageItem> {
    const formData = new FormData();

    for (const key in entry) {
      formData.append(capitalizeFirstLetter(key), entry[key]);
    }

    const targetCompanyId = companyId || this.companyId;
    return this.httpClient.post<IFileStorageItem>(
      this.url + `/api/companies/${targetCompanyId}/dms/submitted-forms/${submittedFormId}/upload`,
      formData
    ).pipe(
      tap(res => this.uploadComplete$.next(res))
    );
  }

  search(searchParams: IFileStorageSearchParams, pagination: IPaginationOptions, companyId?: number): Observable<IFileStoragePaginated> {
    let params = new HttpParams();

    for (const key in searchParams) {
      if (searchParams.hasOwnProperty(key) && searchParams[key]) {
        params = params.set(key, searchParams[key]);
      }
    }

    const targetCompanyId = companyId || this.companyId;
    return this.httpClient.post<IFileStoragePaginated>(
      this.url + `/api/companies/${targetCompanyId}/dms/search`,
      pagination,
      { params: params }
    );
  }

  getDocumentByTypeAndId(documentType: number, documentId: string): Observable<IFileStorageItem[]> {
    return this.httpClient.get<IFileStorageItem[]>
    (this.url + `/api/companies/${this.companyId}/dms/documents/by-source/${documentType}/${documentId}`);
  }

  getDocumentById(documentId: string): Observable<IFileStorageItem> {
    return this.httpClient.get<IFileStorageItem>(
      this.url + `/api/companies/${this.companyId}/dms/documents/${documentId}`
    );
  }

  // Get document for viewing (with inline headers)
  getDocumentForViewing(documentId: string): Observable<Blob> {
    return this.httpClient.get(
      this.url + `/api/companies/${this.companyId}/dms/documents/${documentId}/view`,
      {
        responseType: 'blob',
        headers: {
          'Accept': '*/*'
        }
      }
    );
  }

  // Get document content as blob for inline viewing
  getDocumentBlob(downloadUrl: string): Observable<Blob> {
    return this.httpClient.get(downloadUrl, {
      responseType: 'blob',
      headers: {
        'Accept': '*/*'
      }
    });
  }

  updateDocument(documentId: string, document: Partial<IFileStorageItem>): Observable<IFileStorageItem> {
    return this.httpClient.put<IFileStorageItem>(
      this.url + `/api/companies/${this.companyId}/dms/documents/${documentId}`,
      document
    );
  }

  deleteFile(documentId: string): Observable<void> {
    return this.httpClient.delete<void>(
      this.url + `/api/companies/${this.companyId}/dms/documents/${documentId}`
    );
  }

  shareDocument(documentId: string, shareData: IShareDocumentRequest): Observable<IShareDocumentResponse> {
    return this.httpClient.post<IShareDocumentResponse>(
      this.url + `/api/companies/${this.companyId}/dms/documents/${documentId}/share`,
      shareData
    );
  }

  getDocumentShareLink(documentId: string): Observable<IShareLinkResponse> {
    return this.httpClient.get<IShareLinkResponse>(
      this.url + `/api/companies/${this.companyId}/dms/documents/${documentId}/share-link`
    );
  }

  revokeDocumentShare(documentId: string, shareId: string): Observable<void> {
    return this.httpClient.delete<void>(
      this.url + `/api/companies/${this.companyId}/dms/documents/${documentId}/share/${shareId}`
    );
  }

  // ========== AUTOCOMPLETE METHODS ==========

  getUsedDescriptions(acr: string, code: string): Observable<string[]> {
    return this.httpClient.get<string[]>(
      this.url + `/api/companies/${this.companyId}/dms/acr/${acr}/code/${code}/used-descriptions`
    );
  }

  getUsedTags(acr: string, code: string): Observable<string[]> {
    return this.httpClient.get<string[]>(
      this.url + `/api/companies/${this.companyId}/dms/acr/${acr}/code/${code}/used-tags`
    );
  }
}

// ========== INTERFACES ==========

export interface IShareDocumentRequest {
  emails?: string[];
  expiryDate?: string;
  permissions?: ('view' | 'edit' | 'download')[];
  message?: string;
}

export interface IShareDocumentResponse {
  shareId: string;
  shareLink: string;
  expiryDate?: string;
  permissions: string[];
}

export interface IShareLinkResponse {
  shareLink: string;
  isPublic: boolean;
  expiryDate?: string;
}